import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { animate, springs, useMotionValue } from "../../../lib/motion/index.js";
import { MOBILE_SHEET_HEIGHT_MAX, MOBILE_SHEET_HEIGHT_MIN } from "../../../stores/settings.store.js";

export type SheetMode = "partial" | "full";

/** Fraction of the viewport above which a release snaps to the full state. */
const FULL_AT = 0.82;
/** Fraction below which a release dismisses the sheet. */
const CLOSE_AT = 0.07;
/** Fresh opens show only the handle + title row; users can drag taller. */
const TITLE_PEEK_HEIGHT = 70;
/** Editing an existing task opens at this fraction of the viewport by default. */
const EDIT_OPEN_FRACTION = 0.4;
/** Smallest live drag height before the sheet is considered dismissible. */
const DRAG_MIN_AT = 0.08;
/**
 * Upward fling (px/ms) that snaps to full regardless of height.
 * Raised from 0.55 to 1.0: mobile pointermove fires every 1-5ms, so the
 * instantaneous last-two-event velocity easily spikes above 0.55 on a slow
 * intentional drag, causing accidental full-screen snaps.
 */
const FLING_UP = 1.0;
/** Downward fling (px/ms) that dismisses regardless of height. */
const FLING_DOWN = 0.65;
/** Window (ms) over which velocity is averaged for reliable fling detection. */
const VEL_WINDOW_MS = 150;

interface Args {
  open: boolean;
  reduce: boolean;
  /** Editing an existing task seeds a taller (~40%) sheet vs the create peek. */
  editing: boolean;
  /** Persist a new partial height after the user drags + releases in the band. */
  onCommitPartial: (fraction: number) => void;
  onClose: () => void;
}

interface VelPoint { y: number; t: number; }

interface DragState {
  startY: number;
  startH: number;
  /** Recent pointer positions for averaged velocity calculation. */
  history: VelPoint[];
}

/**
 * Bottom-sheet height gesture. `h` is the live height MotionValue (px); the top
 * handle's pointer drag tracks the finger 1:1, and a release snaps to full /
 * dismisses / settles as a new persisted partial height depending on position
 * and velocity. Fresh opens seed a compact title-only peek.
 *
 * Also listens to visualViewport resize (virtual keyboard open/close). Full
 * sheets track the viewport; partial sheets ignore transient shrinks and grow
 * synchronously on keyboard close so their top edge stays put.
 */
export function useSheetDrag({ open, reduce, editing, onCommitPartial, onClose }: Args) {
  const h = useMotionValue(0);
  const [mode, setMode] = useState<SheetMode>("partial");
  const drag = useRef<DragState | null>(null);
  const modeRef = useRef<SheetMode>("partial");
  const sessionPartialHeight = useRef(TITLE_PEEK_HEIGHT);
  const swallowClickUntil = useRef(0);
  // Latest deps for long-lived window listeners without re-binding.
  const cb = useRef({ onClose, onCommitPartial, reduce, editing });
  cb.current = { onClose, onCommitPartial, reduce, editing };

  useEffect(() => { modeRef.current = mode; }, [mode]);

  // Prefer visualViewport.height: it tracks mobile keyboard changes more
  // accurately than the layout viewport in Android WebView.
  const vh = () => (window.visualViewport?.height ?? window.innerHeight) || 1;

  // Seed the height each time the sheet opens (translateY does the reveal).
  // Layout effect so the panel never paints a 0-height frame first.
  useLayoutEffect(() => {
    if (!open) return;
    setMode("partial");
    const viewportHeight = vh();
    // Edit opens at ~40% of the screen; create stays a compact title-only peek.
    sessionPartialHeight.current = cb.current.editing
      ? Math.min(viewportHeight, viewportHeight * EDIT_OPEN_FRACTION)
      : titlePeekHeight(viewportHeight);
    h.set(sessionPartialHeight.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const tween = useCallback((to: number) => {
    if (cb.current.reduce) { h.set(to); return; }
    animate(h, to, springs.snappy);
  }, [h]);

  const goFull = useCallback(() => {
    setMode("full");
    tween(vh());
  }, [tween]);

  const goPartial = useCallback((fraction: number) => {
    const f = clampFraction(fraction);
    setMode("partial");
    const next = vh() * f;
    sessionPartialHeight.current = next;
    tween(next);
    cb.current.onCommitPartial(f);
  }, [tween]);

  /** Animate back to this open session's partial height WITHOUT persisting. */
  const collapse = useCallback(() => {
    setMode("partial");
    tween(Math.min(vh(), sessionPartialHeight.current));
  }, [tween]);

  /** Grow the current partial sheet enough to reveal newly-needed chrome. */
  const ensureHeight = useCallback((minHeight: number) => {
    if (!Number.isFinite(minHeight) || minHeight <= 0 || modeRef.current === "full") return;
    const next = Math.min(vh(), Math.max(h.get(), minHeight));
    if (next <= h.get() + 1) return;
    sessionPartialHeight.current = next;
    tween(next);
  }, [h, tween]);

  useLayoutEffect(() => {
    if (!open) return;
    const vvp = window.visualViewport;
    let prevVh = vh();

    const onResize = () => {
      if (drag.current) return; // mid-gesture: skip
      const newVh = vh();
      const delta = newVh - prevVh;
      prevVh = newVh;
      if (Math.abs(delta) < 1) return;

      if (modeRef.current === "full") {
        h.set(newVh);
        return;
      }

      if (delta > 0) {
        const next = Math.min(newVh, h.get() + delta);
        h.set(next);
        sessionPartialHeight.current = next;
      }
    };

    vvp?.addEventListener("resize", onResize as EventListener);
    if (!vvp) window.addEventListener("resize", onResize);
    return () => {
      vvp?.removeEventListener("resize", onResize as EventListener);
      if (!vvp) window.removeEventListener("resize", onResize);
    };
  }, [open, h]);

  // Shared drag loop: tracks the finger from `startY` and snaps on release.
  const beginDrag = useCallback((startY: number, captureEl?: HTMLElement) => {
    const now = performance.now();
    drag.current = { startY, startH: h.get(), history: [{ y: startY, t: now }] };

    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      const d = drag.current;
      if (!d) return;
      const t = performance.now();
      // Maintain a rolling history window for averaged velocity on release.
      d.history.push({ y: ev.clientY, t });
      while (d.history.length > 1 && t - d.history[0]!.t > VEL_WINDOW_MS) d.history.shift();
      const viewportHeight = vh();
      const next = Math.max(viewportHeight * DRAG_MIN_AT, Math.min(viewportHeight, d.startH - (ev.clientY - d.startY)));
      h.set(next);
      setMode(next > viewportHeight * FULL_AT ? "full" : "partial");
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      captureEl?.removeEventListener("pointermove", onMove);
      captureEl?.removeEventListener("pointerup", onUp);
      captureEl?.removeEventListener("pointercancel", onUp);
      const d = drag.current;
      drag.current = null;
      if (!d) return;
      const viewportHeight = vh();
      const cur = h.get();
      // Average velocity over the history window (px/ms, positive = moving down).
      let vel = 0;
      const hlen = d.history.length;
      if (hlen >= 2) {
        const first = d.history[0]!;
        const last = d.history[hlen - 1]!;
        const dt = last.t - first.t;
        if (dt > 0) vel = (last.y - first.y) / dt;
      }
      if (vel < -FLING_UP || cur > viewportHeight * FULL_AT) { goFull(); return; }
      if (vel > FLING_DOWN || cur < viewportHeight * CLOSE_AT) { cb.current.onClose(); return; }
      goPartial(cur / viewportHeight);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    captureEl?.addEventListener("pointermove", onMove);
    captureEl?.addEventListener("pointerup", onUp);
    captureEl?.addEventListener("pointercancel", onUp);
  }, [h, goFull, goPartial]);

  const onHandlePointerDown = useCallback((e: ReactPointerEvent) => {
    e.preventDefault();
    // Claim pointer ownership so Android can't re-route the gesture to a
    // scroll container or pull-to-refresh handler mid-drag.
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    beginDrag(e.clientY, e.currentTarget as HTMLElement);
  }, [beginDrag]);

  // Drag the title area (top of the sheet) to resize too, but only after a
  // small vertical threshold, so a plain tap still edits the title / clicks a
  // button. Interactive controls (buttons) opt out so their taps aren't eaten.
  const onGripPointerDown = useCallback((e: ReactPointerEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest("[data-sheet-title]") && target.closest("button, [role=button], a, input, select, textarea")) return;
    const startY = e.clientY;
    const pointerId = e.pointerId;
    const el = e.currentTarget as HTMLElement;
    let armed = false;
    const swallowClick = (ev: MouseEvent) => {
      if (performance.now() <= swallowClickUntil.current) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    };
    const probe = (ev: PointerEvent) => {
      if (armed) return;
      if (Math.abs(ev.clientY - startY) > 8) {
        armed = true;
        cleanup();
        swallowClickUntil.current = performance.now() + 500;
        window.addEventListener("click", swallowClick, true);
        window.setTimeout(() => window.removeEventListener("click", swallowClick, true), 550);
        // Capture with current position (not startY) so the drag starts exactly
        // where the finger is, with no 8px jump and accurate velocity history.
        el.setPointerCapture(pointerId);
        (document.activeElement as HTMLElement | null)?.blur?.();
        beginDrag(ev.clientY, el);
      }
    };
    const cleanup = () => {
      window.removeEventListener("pointermove", probe);
      window.removeEventListener("pointerup", cleanup);
      window.removeEventListener("pointercancel", cleanup);
    };
    window.addEventListener("pointermove", probe);
    window.addEventListener("pointerup", cleanup);
    window.addEventListener("pointercancel", cleanup);
  }, [beginDrag]);

  return { h, mode, onHandlePointerDown, onGripPointerDown, goFull, collapse, ensureHeight };
}

function clampFraction(f: number): number {
  if (!Number.isFinite(f)) return MOBILE_SHEET_HEIGHT_MIN;
  return Math.max(MOBILE_SHEET_HEIGHT_MIN, Math.min(MOBILE_SHEET_HEIGHT_MAX, f));
}

function titlePeekHeight(viewportHeight: number): number {
  return Math.min(viewportHeight, Math.max(TITLE_PEEK_HEIGHT, viewportHeight * DRAG_MIN_AT));
}
